import { Trash } from '@phosphor-icons/react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { TAB_TYPES } from '../../../constants/tab';
import { deletePage, getPage, getPageList, savePage } from '../../../sources/page';
import translations from '../../../translations';
import type { OrderDirection } from '../../../types/api';
import type { PageElementRequestAPI, PageElementResponseAPI } from '../../../types/page';
import { generateRouteUrl, generateTabKey } from '../../../utils/routing';
import { stringReplace } from '../../../utils/string';
import ConfirmModal from '../../generic/modal/ConfirmModal';
import PageHeader from '../../generic/PageHeader';
import Sortable from '../../generic/Sortable';
import Table from '../../generic/Table';
import { useNotificationDispatch } from '../../notifications/NotificationProvider';
import InlineInput from '../../inputs/InlineInput';

interface Props {
    isActive: boolean;
}

interface CellProps {
    item: PageElementResponseAPI;
}

const pageSize = 20;

const PageList = ({ isActive }: Props) => {
    const modalContainerRef = useRef(null);

    const dispatchNotification = useNotificationDispatch();

    const navigate = useNavigate();

    const onNewPageClick = () => {
        const route = generateRouteUrl({
            tabType: TAB_TYPES.newPage,
            options: {
                tabKey: generateTabKey(),
            },
        });

        navigate(route);
    };

    const onRefreshClick = () => {
        fetchPages();
    };

    const onEditClick = (pageId: string) => {
        const route = generateRouteUrl({
            tabType: TAB_TYPES.page,
            options: {
                elementId: pageId,
            },
        });

        navigate(route);
    };

    const onDeleteClick = (page: PageElementResponseAPI) => {
        setSelectedPageElement(page);
        setDeleteModalIsVisible(true);
    };

    const onError = (message: string) => {
        dispatchNotification({
            type: 'notifyError',
            payload: {
                error: message,
            },
        });
    };

    const onDeleteConfirm = async () => {
        if (selectedPageElement) {
            setIsDeleting(true);

            try {
                await deletePage(selectedPageElement.id);
                fetchPages();
            } catch (_error) {
                onError(
                    stringReplace(translations.PAGE_unable_to_delete, {
                        pageName: selectedPageElement?.developerName ?? '',
                    }),
                );
            } finally {
                setIsDeleting(false);
                setDeleteModalIsVisible(false);
                setSelectedPageElement(null);
            }
        }
    };

    const onDeleteCancelClick = () => {
        setDeleteModalIsVisible(false);
        setSelectedPageElement(null);
    };

    const onSortClick = ({
        orderBy,
        direction,
    }: {
        orderBy: string;
        direction: OrderDirection;
    }) => {
        setCurrentPage(1);
        setSortProperty(orderBy);
        setSortDirection(direction);
    };

    const onSearch = (term: string) => {
        setCurrentPage(1);
        setSearchQuery(term);
    };

    const onPageClick = (pageNumber: number) => {
        setCurrentPage(pageNumber);
    };

    const fetchPages = async () => {
        setIsLoading(true);

        try {
            const filter = {
                search: searchQuery,
                page: currentPage,
                orderBy: sortProperty,
                orderDirection: sortDirection,
            };

            const response = await getPageList(filter);

            setPageElements(response.items);
            setPageElementCount(response._meta.total);
            setCurrentPage(response._meta.page);
        } catch (_error) {
            onError(translations.PAGE_unable_to_load_list);
        } finally {
            setIsLoading(false);
        }
    };

    const onSaveSummary = async (pageId: string, developerSummary: string) => {
        try {
            const editedPage = await getPage(pageId);
            await savePage({ ...editedPage, developerSummary } as PageElementRequestAPI);

            fetchPages();
        } catch (_error) {
            onError(translations.PAGE_BUILDER_unable_to_save_page);
        }
    };

    const [pageElements, setPageElements] = useState<PageElementResponseAPI[]>([]);
    const [selectedPageElement, setSelectedPageElement] = useState<PageElementResponseAPI | null>(
        null,
    );

    const [isLoading, setIsLoading] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteModalIsVisible, setDeleteModalIsVisible] = useState(false);
    const [isSummaryEditorOpen, setIsSummaryEditorOpen] = useState<string | null>(null);

    const [searchQuery, setSearchQuery] = useState('');
    const [sortProperty, setSortProperty] = useState('dateModified');
    const [sortDirection, setSortDirection] = useState<OrderDirection>('DESC');

    const [pageElementCount, setPageElementCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor
    useEffect(() => {
        fetchPages();
    }, [searchQuery, currentPage, sortProperty, sortDirection]);

    // biome-ignore lint/correctness/useExhaustiveDependencies: Requires dedicated refactor
    useEffect(() => {
        if (isActive) {
            fetchPages();
        }
    }, [isActive]);

    const columns = [
        {
            renderHeader: () => (
                <Sortable
                    defaultDirection={'ASC'}
                    direction={sortProperty === 'developerName' ? sortDirection : null}
                    onSort={(direction) => onSortClick({ orderBy: 'developerName', direction })}
                >
                    {translations.COMMON_TABLE_name}
                </Sortable>
            ),
            renderCell: ({ item }: CellProps) => (
                <button
                    className="link-emulate"
                    onClick={() => onEditClick(item.id)}
                    aria-label={`Edit ${item.developerName}`}
                    type="button"
                >
                    {item.developerName}
                </button>
            ),
        },
        {
            renderHeader: () => (
                <Sortable
                    defaultDirection={'ASC'}
                    direction={sortProperty === 'dateModified' ? sortDirection : null}
                    onSort={(direction) => onSortClick({ orderBy: 'dateModified', direction })}
                >
                    {translations.COMMON_TABLE_last_modified}
                </Sortable>
            ),
            renderCell: ({ item }: CellProps) =>
                new Date(item.dateModified).toLocaleString(undefined, {
                    dateStyle: 'medium',
                    timeStyle: 'short',
                }),
        },
        {
            renderHeader: () => translations.COMMON_TABLE_summary,
            renderCell: ({ item }: CellProps) => (
                <InlineInput
                    defaultValue={item.developerSummary ?? ''}
                    saveInputValue={(value) => onSaveSummary(item.id, value)}
                    editButtonTitle={`Edit ${item.developerName} summary`}
                    confirmButtonTitle={`Confirm ${item.developerName} summary`}
                    cancelButtonTitle={`Cancel ${item.developerName} summary`}
                    isOpen={isSummaryEditorOpen === item.id}
                    setIsOpen={(isOpen: boolean) => {
                        if (isOpen) {
                            setIsSummaryEditorOpen(item.id);
                        } else {
                            setIsSummaryEditorOpen(null);
                        }
                    }}
                />
            ),
            cellClassName: 'generic-cell-simple',
        },
        {
            renderHeader: () => translations.COMMON_TABLE_actions,
            renderCell: ({ item }: CellProps) => (
                <div className="action-btn-wrapper">
                    <button
                        title={`Delete ${item.developerName}`}
                        className="table-icon table-icon-delete"
                        aria-label={`Delete ${item.developerName}`}
                        onClick={() => onDeleteClick(item)}
                        type="button"
                    >
                        <Trash />
                    </button>
                </div>
            ),
            size: '5rem',
        },
    ];

    return (
        <div className="admin-page list-pages flow-wrapper" ref={modalContainerRef}>
            <ConfirmModal
                show={deleteModalIsVisible}
                title={translations.PAGE_delete_confirmation_title}
                messages={[
                    stringReplace(translations.PAGE_delete_confirmation_message, {
                        pageName: selectedPageElement?.developerName ?? '',
                    }),
                    translations.GENERAL_cannot_be_undone,
                ]}
                buttonStyle="danger"
                buttonCaption="Delete"
                onCancel={onDeleteCancelClick}
                onConfirm={onDeleteConfirm}
                isInProgress={isDeleting}
                container={modalContainerRef.current}
            />
            <PageHeader
                title="Pages"
                onAdd={onNewPageClick}
                onRefresh={onRefreshClick}
                onSearch={onSearch}
                searchQuery={searchQuery}
                searchPlaceholderText="Search"
                addText="New Page"
                refreshTitle={translations.COMMON_refresh_results}
            />
            <Table
                wrapperClassName="margin-top"
                items={pageElements}
                columns={columns}
                isLoading={isLoading}
                rowKeyGenerator={({ id }) => id}
                pagination={{
                    page: currentPage,
                    total: pageElementCount,
                    pageSize,
                    changePage: onPageClick,
                }}
            />
        </div>
    );
};

export default PageList;
